import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Alert, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { useActivePage } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { MetaRecipeProvider, useMetaRecipe } from "@/context";
import {
  MetaCategoryPanel,
  MetaInterestPanel,
  MetaRecipePanel,
  MetaSearchPanel,
} from "@/components/molecules";
import { useActivationRecipe } from "@/hooks/api";
import { activationLogos, getActivationBreadcrumb } from "@/utils";
import { MetaInterest } from "@/types";
import { RecipeSkeleton } from "./RecipeSkeleton";

const RecipeContent: React.FC = () => {
  let { activationId } = useParams<{ activationId: string }>();
  const { activation, categories, interests, loading, error } =
    useActivationRecipe(activationId);

  const readOnly = activation?.shared

  const {selection, setSelection, activeSelection, selectInterest, selectMultiInterest} = useMetaRecipe();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [recipeOpen, setRecipeOpen] = useState<boolean>(true);
  const [recipeError, setRecipeError] = useState<string | null>(null);

  useEffect(() => {
    activation &&
      activation.meta_interests &&
      setSelection(activation.meta_interests);
  }, [activation]);

  const handleSelect = (variable: MetaInterest) => {
    setEditMode(true);
    selectInterest(variable, activeSelection);
    setRecipeOpen(true);
  };

  const handleSelectMulti = (variables: MetaInterest[]) => {
    setEditMode(true);
    selectMultiInterest(variables, activeSelection);
    setRecipeOpen(true);
  };

  const toggleRecipeOpen = () => {
    setRecipeOpen((v) => !v)
  }

  return (
    <PageContainer>
      {loading ? (
        <RecipeSkeleton />
      ) : (
        <>
          <Grid container spacing={3} flexDirection="column" flexGrow={1}>
            {error && (
              <Grid>
                <Alert variant="filled" severity="error">
                  {error}
                </Alert>
              </Grid>
            )}
            {recipeError && (
              <Grid>
                <Alert variant="filled" severity="error">
                  {recipeError}
                </Alert>
              </Grid>
            )}
            {activation && (
              <>
                {/* Activation Name */}
                <Grid size={12}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">
                      {activation.campaign_name}
                    </Typography>
                    {
                      activationLogos[
                        activation.campaign_type as keyof typeof activationLogos
                      ]
                    }
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2" color="textSecondary">
                    Your audience insight has been mapped to interests within
                    the Meta taxonomy. Use the suggestions below to begin
                    building your activation criteria - or adjust them to better
                    suit your campaign goals.
                  </Typography>
                </Grid>

                {/* Interests */}
                <Grid container size={12} spacing={1}>
                  <Grid size={12}>
                    <Typography>Interests</Typography>
                    <Typography variant="body2" color="textSecondary">
                      These are the Meta interests that are most relevant to
                      your audience. Some of them have already been used to
                      populate your Audience Recipe below
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <MetaInterestPanel
                      interests={interests}
                      selection={selection}
                      activeSelection={activeSelection}
                      editMode={editMode}
                      onSelect={handleSelect}
                      onMultiSelect={handleSelectMulti}
                      disabled={readOnly}
                    />
                  </Grid>
                </Grid>

                {/* Categories */}
                <Grid container size={12} spacing={1}>
                  <Grid size={12}>
                    <Typography gutterBottom>Interest Categories</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Here are some top performing categories that contain
                      additional interests that you may want to add to your
                      audience recipe.
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <MetaCategoryPanel
                      categories={categories}
                      selection={selection}
                      activeSelection={activeSelection}
                      editMode={editMode}
                      onSelect={handleSelect}
                      onMultiSelect={handleSelectMulti}
                      disabled={readOnly}
                    />
                  </Grid>
                </Grid>

                {/* Search */}
                {!readOnly &&
                  <Grid container size={12} spacing={1}>
                    <Grid size={12}>
                      <Typography gutterBottom>Interest Search</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Search for additional interests that you know to be
                        relevant for your campaign. Only the top 40 results are
                        shown.
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <MetaSearchPanel
                        selection={selection}
                        activeSelection={activeSelection}
                        editMode={editMode}
                        onSelect={handleSelect}
                        onError={setRecipeError}
                      />
                    </Grid>
                  </Grid>
                }

                {/* Recipe */}
                <MetaRecipePanel
                  activation={activation}
                  onError={setRecipeError}
                  setEditMode={setEditMode}
                  editMode={editMode}
                  open={recipeOpen}
                  toggleOpen={toggleRecipeOpen}
                  disabled={readOnly}
                />
              </>
            )}
          </Grid>
        </>
      )}
    </PageContainer>
  );
};

// The recipe selection only lives on this page
const RecipePage: React.FC = () => (
  <MetaRecipeProvider>
    <RecipeContent />
  </MetaRecipeProvider>
);

export default RecipePage;
